import * as React from 'react';
import * as styled from './styled';
import {useFormikContext} from 'formik';
import {Input, Word} from './components/Input';
import ControlButton, {ControlButtonScale} from 'components/ControlButton';
import {ClassificationGroupObject, TaskObject} from 'api/task/types';
import {IconTrash} from 'icons';
import {useBlockContext} from '../../../../../../hooks/useBlockContext';
import {GroupProps} from './types';
import {Option, OptionVariant} from '../../../../../../../../../../../Option';

/**
 * The group of a classification.
 * @param {GroupProps} props
 * @return {React.ReactElement<GroupProps>}
 * @constructor
 */
export default function Group(
    props: GroupProps,
): React.ReactElement<GroupProps> {
  const {setFieldValue} = useFormikContext<Partial<TaskObject>>();
  const block = useBlockContext();

  // @ts-ignore
  const groups = block?.groups as ClassificationGroupObject[];

  /**
   * Render title.
   * @return {JSX.Element}
   */
  function renderTitle(): JSX.Element {
    /**
     * The change event handler.
     * @param {React.ChangeEvent<HTMLTextAreaElement>} event
     * @return {void}
     */
    function handleChange(
        event: React.ChangeEvent<HTMLTextAreaElement>,
    ): void {
      setFieldValue(
        `content.blocks[${block.index}].groups[${props.index}].title`,
        event.target.value,
      );
    }

    return (
      <styled.Title
        value={props.group.title}
        placeholder="Введите название группы"
        onChange={handleChange}
        required
      />
    );
  }

  /**
   * Render controls.
   * @return {JSX.Element}
   */
  function renderControls(): JSX.Element {
    /**
     * Render delete.
     * @return {JSX.Element}
     */
    function renderDelete(): JSX.Element {
      /**
       * The click event handler.
       * @return {void}
       */
      function handleClick(): void {
        setFieldValue(
          `content.blocks[${block.index}].groups`,
          groups.filter((_, index: number) => (
            index !== props.index
          )),
        );
      }

      return (
        <ControlButton
          type="button"
          scale={ControlButtonScale.Small}
          onClick={handleClick}
        >
          <IconTrash />
        </ControlButton>
      );
    }

    return (
      <styled.Controls>
        {renderDelete()}
      </styled.Controls>
    );
  }

  /**
   * Render words.
   * @return {JSX.Element}
   */
  function renderWords(): JSX.Element {
    /**
     * Render word.
     * @param {string} word
     * @param {number} index
     * @return {JSX.Element}
     */
    function renderWord(
        word: string,
        index: number,
    ): JSX.Element {
      /**
       * The click event handler.
       * @return {void}
       */
      function handleRemove(): void {
        setFieldValue(
          `content.blocks[${block.index}].groups[${props.index}].words`,
          props.group.words.filter((_, _index) => (
            _index !== index
          )),
        );
      }

      return (
        <Option
          key={index}
          variant={OptionVariant.Primary}
          onRemove={handleRemove}
        >
          {word}
        </Option>
      );
    }

    /**
     * Render clear.
     * @return {JSX.Element | undefined}
     */
    function renderClear(): JSX.Element | undefined {
      /**
       * The click event handler.
       * @return {void}
       */
      function handleRemove(): void {
        setFieldValue(
          `content.blocks[${block.index}].groups[${props.index}].words`,
          [],
        );
      }

      return props.group.words.length > 0 ? (
        <Option onRemove={handleRemove}>
          Очистить все
        </Option>
      ) : undefined;
    }

    return (
      <styled.Words>
        {props.group.words.map((word, index) => (
          renderWord(word, index)
        ))}
        {renderClear()}
      </styled.Words>
    );
  }

  /**
   * Render input.
   * @return {JSX.Element}
   */
  function renderInput(): JSX.Element {
    /**
     * The create event handler.
     * @param {Word} word
     * @return {void}
     */
    function handleCreate(word: Word): void {
      setFieldValue(
        `content.blocks[${block.index}].groups[${props.index}].words`,
        [
          ...props.group.words,
          word,
        ],
      );
    }

    return (
      <styled.InputContainer>
        <Input onCreated={handleCreate} />
      </styled.InputContainer>
    );
  }

  return (
    <styled.Wrapper>
      {renderTitle()}
      {renderControls()}
      {renderWords()}
      {renderInput()}
    </styled.Wrapper>
  );
}
